import { Container, Graphics, Rectangle, type Texture } from 'pixi.js';
import type { StoryMod } from '@goodtime/shared';
import { activeMods, MOD_INFO } from '../../logic/story/mods';
import type { SceneLayoutContext } from '../../scenes/SceneComponent';
import { paintedPlate, woodGradient } from '../board/index';
import { display, fitSprite } from './text';

/** How far below the frame's top a chip's middle sits: its top just clears
 *  the free spins banner's bottom edge (machineTop - 5). */
const CHIP_DROP = 14;

/**
 * The reel modifiers running now, and the treasure map pieces found: carved
 * plaques on the top edge of the reel frame, each with its picture and the
 * spins it has left (or the pieces found). Tap one to hear what it does.
 */
export class ModChips extends Container {
  private ctx: SceneLayoutContext | null = null;
  private mods: { mod: StoryMod; spins: number }[] = [];
  private map: { pieces: number; of: number } = { pieces: 0, of: 3 };
  private mapChip: Container | null = null;

  constructor(
    private readonly icon: (mod: StoryMod) => Texture | null,
    private readonly onExplain: (mod: StoryMod, spins: number) => void,
    private readonly mapIcon: () => Texture | null = () => null,
    private readonly onExplainMap: (pieces: number, of: number) => void = () => undefined,
    /** A modifier's name on this slot. */
    private readonly title: (mod: StoryMod) => string = (mod) => MOD_INFO[mod].title,
  ) {
    super();
  }

  /** Redraw with the names as they are now (the slot changed). */
  relabel(): void {
    this.rebuild();
  }

  set(mods: Partial<Record<StoryMod, number>> | undefined): void {
    this.mods = activeMods(mods);
    this.rebuild();
  }

  /** The treasure map pieces found on this slot (no chip while there are none). */
  setMap(pieces: number, of: number): void {
    this.map = { pieces: Math.max(0, pieces), of: Math.max(1, of) };
    this.rebuild();
  }

  /** Where the map chip is (or would be, with `pieces` found), in canvas
   *  pixels: a found piece flies there. */
  mapChipAt(pieces: number): { x: number; y: number } | null {
    const keep = this.map;
    this.setMap(pieces, keep.of);
    const chip = this.mapChip;
    const at = chip ? (() => {
      const b = chip.getBounds();
      return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
    })() : null;
    this.setMap(keep.pieces, keep.of);
    return at;
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.rebuild();
  }

  private rebuild(): void {
    this.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.mapChip = null;
    const ctx = this.ctx;
    if (!ctx || (this.mods.length === 0 && this.map.pieces === 0)) return;
    const left = ctx.machineLeft ?? 0;
    const right = ctx.machineRight ?? ctx.width;
    const chips = this.mods.map(({ mod, spins }) => this.chip(mod, spins));
    if (this.map.pieces > 0) {
      this.mapChip = this.plaque(this.mapIcon(), `MAP ${this.map.pieces}/${this.map.of}`, 'map-chip', () =>
        this.onExplainMap(this.map.pieces, this.map.of),
      );
      chips.push(this.mapChip);
    }
    const gap = 8;
    const total = chips.reduce((s, c) => s + c.width, 0) + gap * (chips.length - 1);
    const scale = Math.min(1, (right - left - 16) / total);
    let x = (left + right) / 2 - (total * scale) / 2;
    for (const c of chips) {
      c.scale.set(scale);
      // On the frame's top rail, clear of the free spins banner, which sits
      // just above the frame (SlotHudComponent: machineTop - 18, 26 high).
      c.position.set(x, ctx.machineTop + CHIP_DROP * scale);
      x += (c.width + gap * scale);
      this.addChild(c);
    }
  }

  private chip(mod: StoryMod, spins: number): Container {
    return this.plaque(this.icon(mod), `${this.title(mod)}  ${spins === 1 ? 'NEXT SPIN' : `${spins} SPINS`}`, `mod-${mod}`, () =>
      this.onExplain(mod, spins),
    );
  }

  private plaque(icon: Texture | null, text: string, name: string, onTap: () => void): Container {
    const c = new Container();
    const label = display(text, 13);
    const art = fitSprite(icon, 26);
    art.anchor.set(0.5);
    // On the painted wood plate (as the wood buttons are), with room for
    // its rounded ends; drawn wood stands in until the art has loaded.
    const h = 32;
    const pad = 16;
    const w = pad + 26 + 6 + label.width + pad;
    let plate: Container | null = paintedPlate('wood', w, h);
    if (plate) {
      plate.position.set(0, -h / 2);
    } else {
      plate = new Graphics()
        .roundRect(0, -h / 2 + 2, w, h, 9)
        .fill({ color: 0x000000, alpha: 0.4 })
        .roundRect(0, -h / 2, w, h, 9)
        .fill(woodGradient())
        .stroke({ color: 0xd9a441, width: 1.5 });
    }
    art.position.set(pad + 13, 0);
    label.anchor.set(0, 0.5);
    label.position.set(pad + 26 + 6, 0);
    c.addChild(plate, art, label);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.hitArea = new Rectangle(0, -22, w, 44);
    c.on('pointertap', onTap);
    c.label = name;
    return c;
  }
}
